import { extractCalendarYear } from './envSchemeRegistry';
import { envYearRangeFromTerms } from './termDateRanges';

/**
 * The period an environmental Rate Year should carry after the reviewer renumbers it — or null
 * to leave the one it has.
 *
 * ⚠️ THE ROW'S PERIOD DOES NOT FOLLOW ITS NEW NUMBER, AND THE POWER % FOLLOWS THE PERIOD.
 *
 * Renumbering moves the whole slot: rate, power %, certificate price and its date_range. That
 * is right when the reviewer is correcting a slot NUMBER the parse got wrong — the AGL
 * off-by-one the control exists for — because there the document's own period is the
 * trustworthy part and must survive untouched.
 *
 * It is wrong when the reviewer is saying "this row IS Year 2". Reported on dev tender
 * 8cdb0db2: SmartestEnergy's LREC and SREC were renumbered year_1 -> year_2 and kept year 1's
 * window, 2027-02-01 -> 2028-01-31. resolvePowerPct reads the calendar year off that start
 * date, so the row went on resolving to 2027 — LREC 19.17%, SREC 12.92% — where the client's
 * model prices Year 2 at 18.98% and 9.27%. Nothing on screen shows a slot's period, so the
 * contradiction was invisible and the environmental figure came out 27% high.
 *
 * The two are told apart by asking both sources the same question. A period that still AGREES
 * with the number it now carries is the document's, and nothing is returned — which is every
 * off-by-one correction, since there the date was right all along. Only a period that
 * CONTRADICTS the new number is stale.
 *
 * Nothing is invented: with no dated contract term to derive from, or with no period on the
 * slot to contradict in the first place, this returns null and the caller leaves the slot as
 * it is rather than giving it a period nobody stated.
 *
 * @param slot         The slot as it now sits under its NEW key.
 * @param siblings     The scheme's other slots — a cumulative schedule is read differently.
 * @param toYearNumber The Rate Year number the slot has just been moved to.
 */
export const rebasedEnvYearRange = (
  slot: Record<string, any> | null | undefined,
  siblings: Record<string, any> | null | undefined,
  contractTerms: Record<string, any> | null | undefined,
  toYearNumber: number,
): { start_date: string; end_date: string } | null => {
  const statedYear = extractCalendarYear(slot, siblings);
  if (statedYear === null) return null;             // no period to contradict

  /**
   * ⚠️ YEAR 1'S REAL END HAS TO COME WITH THE QUESTION.
   *
   * Without it this falls back to twelve-month anniversaries, and on an offer whose Year 1 is
   * longer the renumbered row lands INSIDE Year 1 — the AGL overlap, reintroduced by hand the
   * moment a reviewer re-assigns a row. SOUTH PACIFIC SEEDS: Year 1 runs to 31/12/2027, so a
   * row moved to Year 2 must start 01/01/2028, not 01/10/2027.
   *
   * Only consulted from Year 2 on: a row moved TO Year 1 is the one defining that period, so
   * there is nothing for it to follow.
   */
  const year1Range = siblings?.year_1?.date_range ?? siblings?.year_1?.dateRange;
  const year1End = toYearNumber >= 2
    ? (year1Range?.end_date ?? year1Range?.endDate ?? null)
    : null;

  const implied = envYearRangeFromTerms(contractTerms, toYearNumber, year1End);
  if (!implied) return null;                        // nothing dated to derive from

  const impliedYear = parseInt(String(implied.start_date).slice(0, 4), 10);
  if (!Number.isFinite(impliedYear) || impliedYear === statedYear) return null;

  return implied;
};
